import React, { useState, useEffect, useCallback, useMemo, memo } from 'react';
import { Box, IconButton, Theme, Typography, useTheme } from '@mui/material';
import OpenInFullOutlinedIcon from '@mui/icons-material/OpenInFullOutlined';
import CloseFullscreenOutlinedIcon from '@mui/icons-material/CloseFullscreenOutlined';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { BaseQuestion, FormState, Product } from './types';
import VpRecommendedProductsTable from './VpRecommendedProductsTable';
import VpFilters from './VpFilters';
import { useI18n } from '../../../hooks/useI18n';
import { TranslationKeys } from '../../../i18n/locales/defaultResources';
import { fetchProductsBasedOnAnswers } from '../../../services/varietyPlanner/services';
import { GetProductsParams, ListSortDirection } from './api-types';
import AppTooltip from '../../components/varietyPlanner/AppTooltip';

const makeStyles = (theme: Theme) => ({
  sectionContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'hidden',
  },
  sectionContainerHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  toggleIcon: {
    color: theme.palette.questionsForm.formBlue,
    '&:hover': {
      color: theme.palette.questionsForm.formHoverState,
    },
  },
});

type SortDirection = 'asc' | 'desc' | null;

interface SortState {
  field: string;
  direction: SortDirection;
}

interface VpRecommendedProductsProps {
  formState: FormState;
  isTableSectionInitialView: boolean;
  questions: BaseQuestion[];
  onFormChange: (questionId: string, value: string | string[]) => void;
  handleChangeExpanded: React.Dispatch<React.SetStateAction<boolean>>;
}

const PAGE_SIZE = 30;

const getSelectedAnswersFromFormState = (formState: FormState) => {
  return Object.entries(formState)
    .map(([, value]) => {
      if (!value || (Array.isArray(value) && value.length === 0)) {
        return null;
      }
      return value;
    })
    .filter(Boolean)
    .flat();
};

const VpRecommendedProducts: React.FC<Readonly<VpRecommendedProductsProps>> = ({
  formState,
  isTableSectionInitialView,
  handleChangeExpanded,
  onFormChange,
  questions,
}) => {
  const [sortConfig, setSortConfig] = useState<SortState>({
    field: '',
    direction: null,
  });

  const theme = useTheme();
  const styles = makeStyles(theme);
  const { t } = useI18n();
  //Forced to DE lng, not edtiable in translation modal as per business requirement on this component

  const { t: translationDe } = useTranslation();

  const toggleExpanded = useCallback(() => {
    handleChangeExpanded((prev) => !prev);
  }, [handleChangeExpanded]);

  const handleSort = useCallback((field: string) => {
    setSortConfig((prevSort) => {
      if (prevSort.field === field) {
        if (prevSort.direction === null) return { field, direction: 'desc' };
        if (prevSort.direction === 'desc') return { field, direction: 'asc' };
        return { field: '', direction: null };
      }
      return { field, direction: 'desc' };
    });
  }, []);

  const queryParams = useMemo(() => {
    return {
      filters: getSelectedAnswersFromFormState(formState) as string[],
      sort: sortConfig,
    };
  }, [formState, sortConfig]);

  const { status, data, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: ['products', formState, sortConfig],
      queryFn: async ({ pageParam = 0 }) => {
        const params: GetProductsParams = {
          answers: queryParams.filters,
          skip: pageParam * PAGE_SIZE,
          take: PAGE_SIZE,
          sortDirection: sortConfig.direction
            ? sortConfig.direction === 'asc'
              ? ListSortDirection.Ascending
              : ListSortDirection.Descending
            : undefined,
          sortProperty: queryParams.sort.field || undefined,
        };

        const response = await fetchProductsBasedOnAnswers(params);

        return {
          products: response.products,
          totalCount: response.totalCount,
          pagination: {
            currentPage: pageParam,
            totalPages: Math.ceil(response.totalCount / PAGE_SIZE),
            itemsPerPage: PAGE_SIZE,
            hasMore:
              response.products.length > 0 &&
              (pageParam + 1) * PAGE_SIZE < response.totalCount,
          },
        };
      },
      getNextPageParam: (lastPage) => {
        if (!lastPage.pagination.hasMore) {
          return undefined;
        }

        return lastPage.pagination.currentPage + 1;
      },
      initialPageParam: 0,
      staleTime: 0,
      gcTime: 0,
    });

  const allProducts = useMemo(
    () => data?.pages.flatMap((page) => page.products) || [],
    [data]
  );

  useEffect(() => {
    setSortConfig({ field: '', direction: null });
  }, [formState]);

  return (
    <Box className="modal-second-section" sx={styles.sectionContainer}>
      <Box sx={{ mb: 1 }}>
        <Box sx={styles.sectionContainerHeader}>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Typography
              component="p"
              variant="appH3"
              color="questionsForm.formLabel"
              sx={{ marginRight: 0.75 }}
            >
              {t(TranslationKeys.variety_planner_table_section_title)}
            </Typography>

            <AppTooltip
              //Forced to DE lng, not edtiable in translation modal as per business requirement on this component
              title={translationDe(TranslationKeys.variety_planner_footnotes, {
                lng: 'de',
              })}
            />
          </Box>

          <Box
            sx={{
              display: 'flex',
              justifyContent: { xs: 'flex-start', md: 'center' },
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 1,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: { xs: 'flex-start', md: 'center' },
                alignItems: 'center',
                gap: 0.5,
                flexWrap: 'wrap',
              }}
            >
              <Typography
                variant="appBodyLarge"
                color="questionsForm.formLabel"
              >
                (
              </Typography>
              <Typography
                component="p"
                variant="appBodyLargeBold"
                color="vpTable.recommended"
              >
                {t(TranslationKeys.varietyPlanner_legend_recommended_products)}
                <Typography
                  component="span"
                  variant="appBodyLargeBold"
                  color="questionsForm.formLabel"
                >
                  ,
                </Typography>
              </Typography>
              <Typography
                component="p"
                variant="appBodyLargeBold"
                color="vpTable.neutral"
              >
                {t(TranslationKeys.varietyPlanner_legend_neutral_products)}
                <Typography
                  component="span"
                  variant="appBodyLargeBold"
                  color="questionsForm.formLabel"
                >
                  ,
                </Typography>
              </Typography>

              <Typography
                variant="appBodyLargeBold"
                color="vpTable.notRecommended"
              >
                {t(
                  TranslationKeys.varietyPlanner_legend_not_recommended_products
                )}
              </Typography>
              <Typography
                variant="appBodyLarge"
                color="questionsForm.formLabel"
              >
                )
              </Typography>
            </Box>

            <IconButton
              disableRipple
              onClick={toggleExpanded}
              sx={{ padding: 0 }}
            >
              {isTableSectionInitialView ? (
                <OpenInFullOutlinedIcon sx={styles.toggleIcon} />
              ) : (
                <CloseFullscreenOutlinedIcon sx={styles.toggleIcon} />
              )}
            </IconButton>
          </Box>
        </Box>
        <VpFilters
          formState={formState}
          updateFormState={onFormChange}
          questions={questions}
        />
      </Box>

      <Box sx={{ flexGrow: 1, minHeight: 0 }}>
        <VpRecommendedProductsTable
          rows={allProducts as Product[]}
          loading={status === 'pending'}
          isLoadingMore={isFetchingNextPage}
          error={status === 'error'}
          hasMoreData={!!hasNextPage}
          onLoadMore={fetchNextPage}
          sortConfig={sortConfig}
          onSort={handleSort}
        />
      </Box>
    </Box>
  );
};

export default memo(VpRecommendedProducts);
